import React, {useEffect, useState, Fragment, useLayoutEffect} from "react";
import styled from "styled-components";
import {useNavigate, useNavigationType} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import {
	fetchDetailPageScroll,
	updateOrderPaymentList,
	fetchDetailSummaryInfo
} from "redux/slices/sales/salesSlice";

import {getCommaNumber} from "utils/format";
import {getPaymentTypeName} from "utils/define";
import Observer from "components/ui/Observer";
import Filter from "components/ui/Filter";



/**
 * [기간별 매출 목록]
 */

export const DetailList = ({date, branch, ...props}) => {
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const dispatch = useDispatch();

	const {
		searchPartner,
		searchBranch,
		searchDate,
		searchPeriodType,
		searchFilter,
	} = useSelector(state => state.sales.detailSummaryInfo);

	const {data, pageState} = useSelector(state => state.sales.orderPaymentInfo);
	const {branchUseYn, seqPartnerBranch} = useSelector(state => state.partner);

	useLayoutEffect(() => {
		dispatch(fetchDetailPageScroll("restore"));
	}, []);

	useEffect(() => {
		const isUpdate = (
			!data || navigationType != "POP" || (
				searchPartner != data.searchInfo.searchPartner ||
				searchBranch != data.searchInfo.searchBranch ||
				searchDate != data.searchInfo.searchDate ||
				searchPeriodType != data.searchInfo.searchPeriodType ||
				searchFilter != data.searchInfo.searchFilter
			)
		);
		if(searchDate && searchPeriodType && isUpdate) {
			if(pageState != undefined) {
				dispatch(updateOrderPaymentList());
			}
		}
	}, [searchPartner, searchBranch, searchDate, searchPeriodType, searchFilter]);

	const eventHandler = {
		updateList : function() {
			dispatch(updateOrderPaymentList(true));
		},
		detail : (event) => {
			const target = event.currentTarget;
			const orderType = target.getAttribute("data-type");
			const sequence = Number(target.getAttribute("data-sequence"));
			navigate("/sales/" + orderType + "/payment/" + sequence);
		},
		changeFilter : (name, value) => {
			dispatch(fetchDetailSummaryInfo({
				searchFilter : value
			}));
		}
	};

	const render = () => {
		const getPaymentList = () => {
			let li = ((data || {}).paymentList || []).map(item => {
				// CS25-8980: 취소·대기는 주문 상태가 아니라 결제 행의 표시 상태로 정한다(없으면 결제 상태).
				const status = item.displayStatus || item.paymentStatus;
				const isWait = (status == "WAIT" || status == "PAYMENT_WAIT");
				const isCancel = (status == "CANCELLED" || status == "PAYMENT_CANCELLED");
				// 취소선은 금액이 계산되지 않는 행(낸 금액 0)에만, 음수 금액은 빨간색으로 보인다.
				const isVoid = !isWait && item.paymentAmount != 0 && (item.paidAmount == null ? isCancel : item.paidAmount == 0);
				const orderState = isCancel ? "cancel" : isWait ? "wait" : (item.paymentKind == "REFUND") ? "refund" : "";
				const rowClass = [(isVoid ? "void" : ""), (isWait ? "wait" : ""), (item.paymentAmount < 0 ? "neg" : ""), (orderState == "refund" ? "refund" : "")].join(" ").trim();
				const getSummary = () => {
					const datetime = new Date(item.paymentDatetime).format("sm. sd. sw · hh:MM");
					const memberName = item.name || item.memberName || "-";
					return (
						<Fragment>
							<span>{datetime}</span>
							<span>{memberName}</span>
						</Fragment>
					);
				};
				const getPaymentType = () => {
					const paymentTypeName = getPaymentTypeName(item.paymentType);
					const paymentKind = (orderState == "refund") ? "환불" : (orderState == "cancel") ? "취소" : (orderState == "wait") ? "결제대기" : "결제";
					return paymentTypeName + " " + paymentKind;
				};

				const sequence = item.seqOrderPayment || item.seqRefundInfo;
				const orderType = (item.seqRefundInfo) ? "refund" : "order";
				const orderName = (item.paymentClassification == "TRANSFER") ? "양도 수수료" : (item.orderName || "").recoverTag();
				const paymentKind = (item.webAppDiv == "A") ? "b.pay" : "현장판매";

				return (
					<li key={orderType + "-" + sequence} className={rowClass}>
						<a data-type={orderType} data-sequence={sequence} onClick={eventHandler.detail}>
							<div className="left">
								<h6>{paymentKind}</h6>
								<h4>{orderName || "-"}</h4>
								<h5>{getSummary()}</h5>
							</div>
							<div className="right">
								<h6>&nbsp;</h6>
								<h4>{getCommaNumber(item.paymentAmount)}원</h4>
								<h5>{getPaymentType()}</h5>
							</div>
						</a>
					</li>
				)
			});
			if(!li.length && pageState != undefined) {
				li = <li className="empty">검색 결과가 없습니다.</li>
			}
			return (<ul className="color">{li}</ul>);
		};

		// 결제수단의 이름과 순서는 위쪽 요약(/dashboard/manager/payment)과 맞춘다.
		// 값은 manager-api GetTotalSales.Request.Filter와 맞춘다 — 비페이는 앱 결제 전체(BPAY)가 아니라 MOBILE+PG(BPAY_PG)다.
		const filterList = [{
			label : "전체",
			value : "",
		}, {
			label : "확정 카드 (밴/OFFPG 결제)",
			value : "CONFIRMED_CARD"
		}, {
			label : "키오스크",
			value : "KIOSK"
		}, {
			label : "수기 카드",
			value : "MANUAL_CARD"
		}, {
			label : "비페이 매출",
			value : "BPAY_PG"
		}, {
			label : "수기 이체",
			value : "TRANSFER"
		}, {
			label : "수기 현금",
			value : "CASH"
		}, {
			label : "환불",
			value : "REFUND"
		}];

		const isBranch = (branchUseYn == "Y" && seqPartnerBranch == 0);
		return (
			<Wrapper className={(isBranch) ? "branch" : null}>
				<Filter
					title="판매종류 선택" value={searchFilter}
					options={filterList} onChange={eventHandler.changeFilter}
					isSticky={true}
				/>
				<div className="list">
					{getPaymentList()}
				</div>
				<Observer isActive={pageState} isLoading={true} onVisible={eventHandler.updateList} />
			</Wrapper>
		);
	};
	return render();
};

const Wrapper = styled.div`
.list								{padding:0 24px}
.list li + li						{border-top:1px solid var(--lightBorder)}
.list li.empty						{padding:20px 0; border:none; text-align:center; opacity:0.66}
.list a								{display:flex; align-items:center; justify-content:space-between; padding:25px 0}
.list h4							{margin:5px 0 7px 0; font-size:1rem; font-weight:500}
.list h5							{font-size:var(--13px); color:var(--gray40)}
.list h6							{font-size:var(--12px); color:#7783ad}
.list h5 span + span:before			{content:"|"; margin:0 0.5em; color:var(--gray); opacity:0.5}

.list .left							{}
.list .right						{padding-left:10px; text-align:right}
.list .right h4						{white-space:nowrap; font-size:var(--18px); font-weight:600; color:var(--blue)}
.list li.void .right h4 			{text-decoration:line-through; color:var(--gray40)}
.list li.refund .right h4,
.list li.neg .right h4				{color:var(--red)}
.list li.wait .right h4				{color:var(--gray40)}
.list li.refund .right h4:before	{content:""}
`;

export default DetailList;

